import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail, Phone, MapPin, Check, Instagram, Linkedin, Globe } from "lucide-react";
import { Reveal, Overline, MaskReveal } from "@/components/Motion";
import { LocationsMap } from "@/components/LocationsMap";
import { SEO } from "@/components/SEO";
import { submitEnquiry } from "@/lib/api";
import { SERVICES, SOCIALS } from "@/lib/assets";

const FIELDS = [
  { name: "name", label: "Full name *", type: "text", required: true, col: "md:col-span-6" },
  { name: "company", label: "Company", type: "text", col: "md:col-span-6" },
  { name: "email", label: "Email *", type: "email", required: true, col: "md:col-span-6" },
  { name: "phone", label: "Phone", type: "tel", col: "md:col-span-6" },
  { name: "event", label: "Exhibition / event", type: "text", col: "md:col-span-6" },
  { name: "location", label: "Location", type: "text", col: "md:col-span-6" },
  { name: "stand_size", label: "Stand size (sqm)", type: "text", col: "md:col-span-6" },
  { name: "budget", label: "Estimated budget", type: "text", col: "md:col-span-6" },
];

const empty = { name: "", company: "", email: "", phone: "", service: "", event: "", location: "", stand_size: "", budget: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and project brief.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry(form);
      setDone(true);
      setForm(empty);
      toast.success("Enquiry received. Our team will be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO title="Contact & Request a Quotation | EVO Exhibitions Dubai" description="Request a quotation for your exhibition stand. EVO Exhibitions — exhibition stand contractor in Dubai serving the UAE, Saudi Arabia and the Middle East." path="/contact" />
      <div className="px-6 md:px-12 lg:px-20 pt-40 md:pt-48 pb-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Left */}
        <div className="lg:col-span-5">
          <Overline className="block mb-8">Request a Quotation</Overline>
          <h1 className="font-serif font-light text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-evo-pearl">
            <MaskReveal delay={0.1}>Let's build</MaskReveal>
            <MaskReveal delay={0.25}>something</MaskReveal>
            <MaskReveal delay={0.4}><span className="text-evo-gold italic">extraordinary.</span></MaskReveal>
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-10 text-evo-ash font-light leading-relaxed max-w-md">
              Tell us about your exhibition, your brand and your ambitions. Our team will prepare a tailored proposal for your stand — design, build and delivery.
            </p>
          </Reveal>

          <Reveal delay={0.4}>
            <ul className="mt-14 space-y-5">
              <li className="flex items-center gap-4 text-evo-ash"><Mail size={18} className="text-evo-gold shrink-0" /><span className="text-sm">info@evoexhibitions.com</span></li>
              <li className="flex items-center gap-4 text-evo-ash"><Mail size={18} className="text-evo-gold shrink-0" /><span className="text-sm">sales@evoexhibitions.com</span></li>
              <li className="flex items-center gap-4 text-evo-ash"><Phone size={18} className="text-evo-gold shrink-0" /><span className="text-sm">+39 347 296 1122</span></li>
            </ul>
            <div className="mt-10">
              <p className="text-[11px] uppercase tracking-[0.25em] text-evo-gold mb-5">Locations</p>
              <ul className="space-y-4">
                <li className="flex items-start gap-4 text-evo-ash"><MapPin size={18} className="text-evo-gold mt-0.5 shrink-0" /><span className="text-sm">Italy — Headquarters</span></li>
                <li className="flex items-start gap-4 text-evo-ash"><MapPin size={18} className="text-evo-gold mt-0.5 shrink-0" /><span className="text-sm">Dubai, United Arab Emirates — Office</span></li>
                <li className="flex items-start gap-4 text-evo-ash"><MapPin size={18} className="text-evo-gold mt-0.5 shrink-0" /><span className="text-sm">Saudi Arabia — Regional Operations &amp; Project Presence</span></li>
              </ul>
            </div>
          </Reveal>
          <div className="mt-12">
            <p className="text-[11px] uppercase tracking-[0.25em] text-evo-gold mb-5">Follow EVO</p>
            <ul className="space-y-4">
              <li><a href={SOCIALS.website} target="_blank" rel="noopener noreferrer" data-testid="contact-website" className="inline-flex items-center gap-3 text-evo-ash hover:text-evo-gold transition-colors text-sm"><Globe size={17} className="text-evo-gold" /> {SOCIALS.websiteLabel}</a></li>
              <li><a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="inline-flex items-center gap-3 text-evo-ash hover:text-evo-gold transition-colors text-sm"><Instagram size={17} className="text-evo-gold" /> {SOCIALS.instagramLabel}</a></li>
              <li><a href={SOCIALS.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin" className="inline-flex items-center gap-3 text-evo-ash hover:text-evo-gold transition-colors text-sm"><Linkedin size={17} className="text-evo-gold" /> {SOCIALS.linkedinLabel}</a></li>
            </ul>
          </div>
        </div>

        {/* Right — form */}
        <div className="lg:col-span-6 lg:col-start-7">
          {done ? (
            <motion.div
              data-testid="enquiry-success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-evo-gold/30 bg-evo-marble1 p-12 md:p-16 text-center h-full flex flex-col items-center justify-center"
            >
              <div className="w-16 h-16 rounded-full border border-evo-gold flex items-center justify-center mb-8">
                <Check size={28} className="text-evo-gold" />
              </div>
              <h2 className="font-serif text-3xl md:text-4xl text-evo-pearl mb-4">Enquiry received</h2>
              <p className="text-evo-ash font-light max-w-sm">Thank you. Our team will review your brief and respond with a tailored proposal shortly.</p>
              <button
                data-testid="new-enquiry-btn"
                onClick={() => setDone(false)}
                className="mt-10 link-underline text-xs uppercase tracking-[0.2em] text-evo-gold"
              >
                Submit another enquiry
              </button>
            </motion.div>
          ) : (
            <form data-testid="enquiry-form" onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-10">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.col}>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-evo-ash mb-3">{f.label}</label>
                  <input
                    data-testid={`field-${f.name}`}
                    type={f.type}
                    value={form[f.name]}
                    onChange={update(f.name)}
                    required={f.required}
                    className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-3 text-evo-pearl placeholder-white/30 focus:border-evo-gold focus:outline-none transition-colors duration-300"
                  />
                </div>
              ))}

              <div className="md:col-span-12">
                <label className="block text-[11px] uppercase tracking-[0.2em] text-evo-ash mb-3">Service of interest</label>
                <select
                  data-testid="field-service"
                  value={form.service}
                  onChange={update("service")}
                  className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-3 text-evo-pearl focus:border-evo-gold focus:outline-none transition-colors duration-300"
                >
                  <option value="" className="bg-evo-marble1">Select a service</option>
                  {SERVICES.map((s) => (
                    <option key={s.n} value={s.title} className="bg-evo-marble1">{s.title}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-12">
                <label className="block text-[11px] uppercase tracking-[0.2em] text-evo-ash mb-3">Project brief *</label>
                <textarea
                  data-testid="field-message"
                  value={form.message}
                  onChange={update("message")}
                  required
                  rows={4}
                  className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-3 text-evo-pearl placeholder-white/30 focus:border-evo-gold focus:outline-none transition-colors duration-300 resize-none"
                  placeholder="Tell us about your stand, objectives and timeline…"
                />
              </div>

              <div className="md:col-span-12 pt-2">
                <button
                  data-testid="submit-enquiry-btn"
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-3 bg-evo-gold text-evo-black hover:bg-evo-goldsoft transition-colors duration-300 px-10 py-5 text-xs uppercase tracking-[0.2em] group disabled:opacity-60"
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Submit Enquiry <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      </div>
      <LocationsMap />
    </div>
  );
};

export default Contact;
